import { useMemo, useCallback } from 'react'
import type { MindMapNode, MindMapEdge } from '@bandos/shared'
import type { SectionKey } from '../../reportSections'
import {
  getRootStandaloneIdeas,
  getRootNeeds,
  getChildNeeds,
  filterNodesByType,
} from '../../../utils/reportHierarchyUtils'

export function useReportHierarchy(
  nodes: MindMapNode[],
  edges: MindMapEdge[],
  nodeMap: Map<string, MindMapNode>,
  getEffectiveNodeType: (node: MindMapNode) => string,
  effectiveShouldDisplayNode: (nodeId: string) => boolean,
  getChildIdeas: (parentId: string) => MindMapNode[],
  isVisible: (key: SectionKey) => boolean
) {
  // Build hierarchy config for utility functions
  const hierarchyConfig = useMemo(() => {
    if (nodes.length === 0) return null
    return {
      nodes,
      edges,
      getEffectiveNodeType,
      shouldDisplayNode: effectiveShouldDisplayNode,
    }
  }, [nodes, edges, getEffectiveNodeType, effectiveShouldDisplayNode])

  // Find ALL venture nodes (including collapsed/summarized ones)
  const ventureNodes = useMemo(() => {
    if (!hierarchyConfig) return []
    return filterNodesByType(hierarchyConfig.nodes, 'ventureNode', hierarchyConfig)
  }, [hierarchyConfig])

  // Find standalone unmet needs (not under any venture)
  const standaloneNeeds = useMemo(() => {
    if (!hierarchyConfig) return []
    return getRootNeeds(hierarchyConfig)
  }, [hierarchyConfig])

  // Find root standalone ideas, sorted by visual weight asc (solo nodes first)
  const standaloneIdeas = useMemo(() => {
    if (!hierarchyConfig) return []
    const ideas = getRootStandaloneIdeas(hierarchyConfig)
    // Recursive visual weight: each idea child = 1, storyboard/survey = 10
    const nodeScore = (id: string, visited = new Set<string>()): number => {
      if (visited.has(id)) return 0
      const next = new Set(visited).add(id)
      return edges
        .filter((e) => e.source === id)
        .reduce((sum, e) => {
          const n = nodeMap.get(e.target)
          if (!n) return sum
          const effectiveType = n.type === 'summarizedNode' ? getEffectiveNodeType(n) : n.type
          if (effectiveType === 'ideaNode') return sum + 1 + nodeScore(n.id, next)
          if (effectiveType === 'storyboardNode') return sum + 10
          return sum
        }, 0)
    }
    return ideas.sort((a, b) => nodeScore(a.id) - nodeScore(b.id))
  }, [hierarchyConfig, edges, nodeMap, getEffectiveNodeType])

  // Helper to get child unmet needs of a venture
  const getVentureNeeds = useCallback(
    (ventureId: string): MindMapNode[] => {
      if (!hierarchyConfig) return []
      return getChildNeeds(ventureId, hierarchyConfig)
    },
    [hierarchyConfig]
  )

  // All ideas across a venture's needs (used when needs are hidden)
  const getAllIdeasUnderVenture = useCallback(
    (ventureId: string): MindMapNode[] =>
      getVentureNeeds(ventureId).flatMap((n) => getChildIdeas(n.id)),
    [getVentureNeeds, getChildIdeas]
  )

  // Ideas from standalone needs (shown when needs are hidden so they don't disappear)
  const ideasFromStandaloneNeeds = useMemo(
    () =>
      !isVisible('need') && (isVisible('idea') || isVisible('storyboard') || isVisible('survey'))
        ? standaloneNeeds.flatMap((n) => getChildIdeas(n.id))
        : [],
    [isVisible, standaloneNeeds, getChildIdeas]
  )

  return {
    ventureNodes,
    standaloneNeeds,
    standaloneIdeas,
    getVentureNeeds,
    getAllIdeasUnderVenture,
    ideasFromStandaloneNeeds,
  }
}
